import React, { useState } from 'react';
import { useUIStore } from '../../../store/useUIStore';

export const SeismicAnalysis: React.FC = () => {
  const [seismicOptions, setSeismicOptions] = useState({
    codeStandard: 'IBC2018' as 'IBC2018' | 'ASCE7' | 'UBC97' | 'IS1893',
    siteClass: 'D' as 'A' | 'B' | 'C' | 'D' | 'E' | 'F',
    importanceFactor: 1.0,
    responseModificationFactor: 3.5,
    spectralAcceleration: {
      ss: 1.5,
      s1: 0.6
    },
    analysisMethod: 'equivalent' as 'equivalent' | 'response_spectrum' | 'time_history'
  });

  const { showNotification } = useUIStore();

  const handleRunAnalysis = () => {
    showNotification('info', 'Seismic Analysis', 'Seismic analysis initiated');
    
    setTimeout(() => {
      showNotification('success', 'Analysis Complete', 'Seismic analysis completed successfully');
    }, 2500);
  };

  return (
    <div className="seismic-analysis-panel">
      <div className="panel-header">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Seismic Analysis</h3>
      </div>
      
      <div className="panel-content space-y-6">
        <div className="code-selection">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Building Code</h4>
          
          <select
            value={seismicOptions.codeStandard}
            onChange={(e) => setSeismicOptions({
              ...seismicOptions,
              codeStandard: e.target.value as any
            })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="IBC2018">IBC 2018</option>
            <option value="ASCE7">ASCE 7-16</option>
            <option value="UBC97">UBC 1997</option>
            <option value="IS1893">IS 1893:2016</option>
          </select>
        </div>

        <div className="site-parameters">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Site Parameters</h4>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Site Class
              </label>
              <select
                value={seismicOptions.siteClass}
                onChange={(e) => setSeismicOptions({
                  ...seismicOptions,
                  siteClass: e.target.value as any
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="A">Site Class A - Hard Rock</option>
                <option value="B">Site Class B - Rock</option>
                <option value="C">Site Class C - Very Dense Soil</option>
                <option value="D">Site Class D - Stiff Soil</option>
                <option value="E">Site Class E - Soft Soil</option>
                <option value="F">Site Class F - Special Study Required</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ss (g)
                </label>
                <input
                  type="number"
                  value={seismicOptions.spectralAcceleration.ss}
                  onChange={(e) => setSeismicOptions({
                    ...seismicOptions,
                    spectralAcceleration: {
                      ...seismicOptions.spectralAcceleration,
                      ss: parseFloat(e.target.value)
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  S1 (g)
                </label>
                <input
                  type="number"
                  value={seismicOptions.spectralAcceleration.s1}
                  onChange={(e) => setSeismicOptions({
                    ...seismicOptions,
                    spectralAcceleration: {
                      ...seismicOptions.spectralAcceleration,
                      s1: parseFloat(e.target.value)
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="structure-parameters">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Structure Parameters</h4>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Importance Factor (I)
              </label>
              <input
                type="number"
                value={seismicOptions.importanceFactor}
                onChange={(e) => setSeismicOptions({
                  ...seismicOptions,
                  importanceFactor: parseFloat(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.8"
                max="1.5"
                step="0.1"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Response Modification Factor (R)
              </label>
              <input
                type="number"
                value={seismicOptions.responseModificationFactor}
                onChange={(e) => setSeismicOptions({
                  ...seismicOptions,
                  responseModificationFactor: parseFloat(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max="8"
                step="0.5"
              />
            </div>
          </div>
        </div>

        <div className="analysis-method">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Analysis Method</h4>
          
          <div className="space-y-2">
            {[
              { value: 'equivalent', label: 'Equivalent Static Force' },
              { value: 'response_spectrum', label: 'Response Spectrum Analysis' },
              { value: 'time_history', label: 'Time History Analysis' }
            ].map((method) => (
              <label key={method.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="analysisMethod"
                  value={method.value}
                  checked={seismicOptions.analysisMethod === method.value}
                  onChange={(e) => setSeismicOptions({
                    ...seismicOptions,
                    analysisMethod: e.target.value as any
                  })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {method.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="analysis-actions">
          <button
            onClick={handleRunAnalysis}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Run Seismic Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeismicAnalysis;
